import { type MaybeRef, type Ref, computed, unref } from "vue";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { ONE_MINUTE_MS } from "@/helpers/constants";
import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import borrowerPlatformTemplateService from "@/services/modules/borrowerPlatformTemplates";

import type {
  BorrowerPlatformTemplate,
  BorrowerPlatformTemplateFile,
  BorrowerPlatformTemplateFilters,
  BorrowerPlatformTemplateListItem,
  CreateBorrowerPlatformTemplateRequest,
  UpdateBorrowerPlatformTemplateRequest,
} from "@/models/borrowerPlatformTemplates";
import type { IPaginatedResponse } from "@/models/common";

export const useBorrowerPlatformTemplatesQuery = (
  params?: Ref<BorrowerPlatformTemplateFilters>
) => {
  const queryKey = computed(() =>
    QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.list(params?.value)
  );

  return useQuery({
    queryKey,
    queryFn: (): Promise<
      IPaginatedResponse<BorrowerPlatformTemplateListItem>
    > =>
      borrowerPlatformTemplateService.getBorrowerPlatformTemplates({
        payload: params?.value ?? {},
      }),
    staleTime: 2 * ONE_MINUTE_MS,
  });
};

export const useBorrowerPlatformTemplateQuery = (id: MaybeRef<string>) => {
  const templateId = computed(() => unref(id));
  const queryKey = computed(() =>
    QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.detail(templateId.value)
  );

  const enabled = computed(() => !!templateId.value);

  return useQuery({
    queryKey,
    queryFn: (): Promise<BorrowerPlatformTemplate> =>
      borrowerPlatformTemplateService.getBorrowerPlatformTemplate(
        templateId.value
      ),
    enabled,
    staleTime: 5 * ONE_MINUTE_MS,
    gcTime: 10 * ONE_MINUTE_MS,
  });
};

export const useCreateBorrowerPlatformTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: CreateBorrowerPlatformTemplateRequest
    ): Promise<BorrowerPlatformTemplate> =>
      borrowerPlatformTemplateService.createBorrowerPlatformTemplate({
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(),
      });
    },
  });
};

export const useUpdateBorrowerPlatformTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: UpdateBorrowerPlatformTemplateRequest
    ): Promise<BorrowerPlatformTemplate | undefined> => {
      return borrowerPlatformTemplateService.updateBorrowerPlatformTemplate({
        payload,
      });
    },
    onSuccess: (
      data: BorrowerPlatformTemplate | undefined,
      variables: UpdateBorrowerPlatformTemplateRequest
    ) => {
      if (data && variables.id) {
        queryClient.setQueryData(
          QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.detail(variables.id),
          data
        );

        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(),
        });
      }
    },
  });
};

export const useDeleteBorrowerPlatformTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      borrowerPlatformTemplateService.deleteBorrowerPlatformTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(),
      });
    },
  });
};

export const useDuplicateBorrowerPlatformTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string): Promise<BorrowerPlatformTemplate> =>
      borrowerPlatformTemplateService.duplicateBorrowerPlatformTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(),
      });
    },
  });
};

export const useToggleGlobalStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string): Promise<BorrowerPlatformTemplate> =>
      borrowerPlatformTemplateService.toggleGlobalStatus(id),
    onSuccess: (data: BorrowerPlatformTemplate) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.detail(data.id),
      });
    },
  });
};

export const useUploadBorrowerPlatformTemplateFileMutation = () => {
  return useMutation({
    mutationFn: ({
      templateId,
      file,
    }: {
      templateId: string;
      file: File;
    }): Promise<BorrowerPlatformTemplateFile> =>
      borrowerPlatformTemplateService.uploadFile(templateId, file),
  });
};

export const useDeleteBorrowerPlatformTemplateFileMutation = () => {
  return useMutation({
    mutationFn: ({
      templateId,
      fileId,
    }: {
      templateId: string;
      fileId: string;
    }) => borrowerPlatformTemplateService.deleteFile(templateId, fileId),
  });
};

export const useBorrowerPlatformTemplateFileQuery = (
  templateId: MaybeRef<string>,
  fileId: MaybeRef<string | number>
) => {
  const computedTemplateId = computed(() => unref(templateId));
  const computedFileId = computed(() => unref(fileId));

  const enabled = computed(() => {
    return !!computedTemplateId.value && !!computedFileId.value;
  });

  return useQuery({
    queryKey: computed(() => [
      "borrower-platform-template-file",
      computedTemplateId.value,
      computedFileId.value,
    ]),
    queryFn: (): Promise<BorrowerPlatformTemplateFile> =>
      borrowerPlatformTemplateService.getFile(
        computedTemplateId.value,
        computedFileId.value
      ),
    enabled,
  });
};
